import { useEffect, useLayoutEffect, useRef } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { gsap, registerGsap } from '../../anim/gsapSetup'
import { Logo } from '../common/Logo'
import { Button } from '../common/Button'
import { Icon } from '../common/Icon'
import { primaryNav } from '../../data/navigation'
import { company } from '../../data/company'
import { stopScroll, startScroll } from '../../anim/smoothScroll'
import { cn } from '../../lib/cn'
import { useAuthStore } from '../../../store/auth'

interface MobileMenuProps {
  open: boolean
  onClose: () => void
}

export function MobileMenu({ open, onClose }: MobileMenuProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const firstFocusRef = useRef<HTMLButtonElement>(null)
  const isAuthed = useAuthStore((s) => !!s.accessToken && !!s.user)

  // reveal animation + scroll lock
  useLayoutEffect(() => {
    if (!open) return
    registerGsap()
    stopScroll()
    document.body.style.overflow = 'hidden'
    const ctx = gsap.context(() => {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (reduced) {
        gsap.set('[data-mm="panel"]', { opacity: 1 })
        return
      }
      gsap
        .timeline()
        .from('[data-mm="panel"]', { yPercent: -100, duration: 0.5, ease: 'power4.out' })
        .from('[data-mm="item"]', { y: 24, opacity: 0, duration: 0.5, stagger: 0.06, ease: 'power3.out' }, '-=0.2')
        .from('[data-mm="foot"]', { opacity: 0, duration: 0.4 }, '-=0.2')
    }, panelRef)
    firstFocusRef.current?.focus()
    return () => {
      ctx.revert()
      document.body.style.overflow = ''
      startScroll()
    }
  }, [open])

  // keyboard: Esc + focus trap
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'Tab') {
        const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )
        if (!focusables || focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div ref={panelRef} className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" aria-label="Site menu">
      <div
        data-mm="panel"
        className="grain absolute inset-0 flex flex-col overflow-y-auto bg-night text-white"
        style={{ ['--leaf-vein' as string]: '#06231A' }}
      >
        <div className="container-x flex items-center justify-between py-4">
          <Link to="/" onClick={onClose} aria-label="GreenbayBio home">
            <Logo tone="light" />
          </Link>
          <button
            ref={firstFocusRef}
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-white hover:bg-white/10"
          >
            <Icon name="X" className="h-6 w-6" />
          </button>
        </div>

        <nav aria-label="Mobile" className="container-x flex flex-1 flex-col justify-center gap-1 py-8">
          {primaryNav.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              onClick={onClose}
              data-mm="item"
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-between border-b border-white/10 py-4 font-display text-3xl font-medium transition-colors',
                  isActive ? 'text-lime-bright' : 'text-white hover:text-lime',
                )
              }
            >
              {link.label}
              <Icon name="ArrowUpRight" className="h-6 w-6 text-white/40" />
            </NavLink>
          ))}
        </nav>

        <div data-mm="foot" className="container-x space-y-4 border-t border-white/10 py-8">
          {isAuthed ? (
            <Button to="/home" variant="ghost-light" size="lg" className="w-full" onClick={onClose}>
              <Icon name="Gauge" className="h-5 w-5" />
              Dashboard
            </Button>
          ) : (
            <Button to="/login" variant="ghost-light" size="lg" className="w-full" onClick={onClose}>
              Login
            </Button>
          )}
          <Button to="/contact" variant="primary" size="lg" className="w-full" onClick={onClose}>
            Enquire now
            <Icon name="ArrowRight" className="h-5 w-5" />
          </Button>
          <div className="flex flex-col gap-2 text-sm text-white/70">
            <a href={`tel:${company.phoneHref}`} className="inline-flex items-center gap-2 hover:text-white">
              <Icon name="Phone" className="h-4 w-4" /> {company.phoneDisplay}
            </a>
            <a href={`mailto:${company.email}`} className="inline-flex items-center gap-2 hover:text-white">
              <Icon name="Mail" className="h-4 w-4" /> {company.email}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
